import React, { useCallback, useEffect, useRef, useState } from "react";
import "./css/login.css";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { checkSession } from "./common/session";

// 앱 환경 감지 (RN WebView 등)
const isAppEnvironment = () => {
    if (typeof window === "undefined") return false;
    if (window.ReactNativeWebView) return true;
    if (window.isNativeApp || window.__MY_CERT_USER_DATA__) return true;
    const ua = navigator.userAgent || "";
    return /wv/.test(ua) || (/Android/i.test(ua) && /Version\//.test(ua));
};

// 숫자 배열 섞기 (Fisher–Yates)
const shuffleArray = (arr) => {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
};

export default function Password() {
    const navigate = useNavigate();
    const location = useLocation();
    
    // 비밀번호 모드: 'set' (설정), 'confirm' (확인), 'enter' (입력), 'biometric_register' (생체인식 등록 - 앱 전용)
    const [passwordMode, setPasswordMode] = useState(null);
    const [pin, setPin] = useState([]);
    const [firstOtp, setFirstOtp] = useState(null);
    const [error, setError] = useState("");
    
    // 키패드 숫자 상태 (0~9)
    const [keypadNumbers, setKeypadNumbers] = useState([0, 1, 2, 3, 4, 5, 6, 7, 8, 9]);
    
    // 사용자 정보
    const [userName, setUserName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [userStatus, setUserStatus] = useState(null);
    
    // 마지막으로 받은 구글 토큰/프로필 (토큰 재발급 시 사용)
    const lastTokenRef = useRef({ idToken: null, accessToken: null });
    const lastProfileRef = useRef(null);
    
    // 지문인식 관련 상태
    const [isBiometricDeviceAvailable, setIsBiometricDeviceAvailable] = useState(false); // 디바이스 지원 여부
    const [isBiometricRegistered, setIsBiometricRegistered] = useState(false); // DB에 등록 여부
    const [biometricError, setBiometricError] = useState("");
    
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";

    // 페이지 로드 시 세션 확인 및 비밀번호 모드 결정
    useEffect(() => {
        const initializePasswordPage = async () => {
            const isApp = isAppEnvironment();
            
            // location.state에서 userStatus 가져오기 (활성화 직후인지 확인)
            const statusFromState = location.state?.userStatus;
            const isAfterActivate = statusFromState === "active" || location.state?.isAfterActivate === true;
            
            // 활성화 직후가 아니고 앱 환경이 아닐 때만 세션 확인
            if (!isApp && !isAfterActivate) {
                // 웹 브라우저에서 활성화 직후가 아니면 세션 확인
                try {
                    const sessionResult = await checkSession();
                    if (!sessionResult.isValid || sessionResult.status !== 'active') {
                        console.log("❌ 세션 없음 또는 status가 active가 아님 - 로그인 페이지로 이동 (웹 브라우저)");
                        navigate("/login", { replace: true });
                        return;
                    }
                    console.log("✅ 세션 확인 완료 - 비밀번호 페이지 계속 진행");
                } catch (error) {
                    console.error("❌ 세션 확인 중 오류 발생:", error);
                    console.log("❌ 세션 확인 오류 - 로그인 페이지로 이동");
                    navigate("/login", { replace: true });
                    return;
                }
            } else {
                // 앱(RN WebView)이거나 활성화 직후인 경우 세션 체크 생략
                if (isApp) {
                    console.log("📱 앱 환경 - 세션 체크로 리다이렉트하지 않고 비밀번호 화면 유지");
                    // RN 환경에서도 회원정보가 있는지 확인 (없으면 로그인 페이지로 이동)
                    const savedEmail = localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail");
                    if (!savedEmail) {
                        console.log("❌ RN 환경 - 회원정보 없음 - 로그인 페이지로 이동");
                        navigate("/login", { replace: true });
                        return;
                    }
                    // 회원가입 확인은 로그인 페이지에서 처리하므로 여기서는 확인하지 않음
                }
                if (isAfterActivate) {
                    console.log("✅ 활성화 직후 - 세션 체크 생략하고 비밀번호 화면 유지");
                    // 활성화 직후에는 sessionStorage에 세션 토큰이 있는지 확인만
                    const sessionToken = sessionStorage.getItem('hb_session_token');
                    if (sessionToken) {
                        console.log("✅ 활성화 직후 세션 토큰 확인:", sessionToken.substring(0, 16) + '...');
                    } else {
                        console.warn("⚠️ 활성화 직후 세션 토큰이 없음 (하지만 계속 진행)");
                    }
                }
            }
            
            // 세션이 있으면 비밀번호 모드 결정
            const savedEmail = localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail");
            const savedName = localStorage.getItem("hb_serverName") || localStorage.getItem("hb_googleName");
            
            if (savedEmail) setUserEmail(savedEmail);
            if (savedName) setUserName(savedName);
            
            // location.state에서 userStatus 가져오기
            if (statusFromState) setUserStatus(statusFromState);
            
            // DB에서 OTP 존재 여부 확인 (API 호출)
            if (savedEmail) {
                try {
                    const checkRes = await fetch(`${API_BASE_URL}/auth/otp/check?email=${encodeURIComponent(savedEmail)}`, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                        },
                    });
                    
                    if (checkRes.ok) {
                        const checkData = await checkRes.json();
                        // OTP가 있으면 입력 모드, 없으면 설정 모드
                        if (checkData.hasOtp) {
                            setPasswordMode("enter");
                            
                            // 앱 환경이고 생체인식이 등록되어 있으면 생체인식 사용 가능 여부 확인
                            if (isApp) {
                                checkBiometricRegistered(savedEmail);
                            }
                        } else {
                            setPasswordMode("set");
                        }
                    } else {
                        // API 호출 실패 시 기본값으로 설정 모드
                        console.warn("OTP 확인 API 호출 실패, 설정 모드로 진행");
                        setPasswordMode("set");
                    }
                } catch (error) {
                    console.error("OTP 확인 중 오류:", error);
                    // 오류 발생 시 기본값으로 설정 모드
                    setPasswordMode("set");
                }
            } else {
                // 이메일이 없으면 설정 모드
                setPasswordMode("set");
            }
            
            // 키패드 숫자 섞기
            setKeypadNumbers(shuffleArray([0, 1, 2, 3, 4, 5, 6, 7, 8, 9]));
            
            // 앱 환경에서 지문인식 사용 가능 여부 확인
            if (isApp) {
                checkBiometricAvailability();
            }
        };
        
        initializePasswordPage();
    }, [navigate, location.state]);
    
    // 지문인식 사용 가능 여부 확인 (디바이스 지원 여부)
    const checkBiometricAvailability = () => {
        if (window.ReactNativeWebView) {
            console.log('🔍 [password.js] 지문인식 사용 가능 여부 확인 요청');
            // RN WebView에서 지문인식 사용 가능 여부 확인 요청
            window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'CHECK_BIOMETRIC_AVAILABILITY',
            }));
        } else {
            console.warn('⚠️ [password.js] window.ReactNativeWebView가 없습니다.');
        }
    };

    // 생체인식 등록 여부 확인 (DB에서 확인)
    const checkBiometricRegistered = async (email) => {
        try {
            const checkRes = await fetch(`${API_BASE_URL}/auth/biometric/check?email=${encodeURIComponent(email)}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });
            
            if (checkRes.ok) {
                const checkData = await checkRes.json();
                if (checkData.isRegistered) {
                    console.log('✅ [password.js] 생체인식이 등록되어 있음');
                    setIsBiometricRegistered(true);
                } else {
                    console.log('ℹ️ [password.js] 생체인식이 등록되어 있지 않음');
                    setIsBiometricRegistered(false);
                }
            }
        } catch (error) {
            console.error("생체인식 등록 여부 확인 중 오류:", error);
            setIsBiometricRegistered(false);
        }
    };
    
    
    // 지문인식 요청 함수 (로그인용)
    const requestFingerprint = () => {
        if (!isAppEnvironment() || !window.ReactNativeWebView) {
            console.warn('⚠️ [password.js] 지문인식 요청 실패 - 환경 또는 WebView 없음');
            setBiometricError('지문인식을 사용할 수 없습니다.');
            return;
        }
        
        console.log('👆 [password.js] 지문인식 요청 전송 (로그인)');
        setBiometricError("");
        window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'REQUEST_FINGERPRINT',
        }));
    };

    // 생체인식 등록 요청 함수
    const requestBiometricRegister = () => {
        console.log('🔍 [password.js] requestBiometricRegister 호출');
        console.log('   - isAppEnvironment():', isAppEnvironment());
        console.log('   - window.ReactNativeWebView:', !!window.ReactNativeWebView);
        console.log('   - window.ReactNativeWebView 타입:', typeof window.ReactNativeWebView);
        
        if (!isAppEnvironment()) {
            console.warn('⚠️ [password.js] 앱 환경이 아님');
            setBiometricError('앱 환경에서만 사용할 수 있습니다.');
            return;
        }
        
        if (!window.ReactNativeWebView) {
            console.warn('⚠️ [password.js] window.ReactNativeWebView가 없음');
            setBiometricError('ReactNativeWebView를 사용할 수 없습니다.');
            return;
        }
        
        if (typeof window.ReactNativeWebView.postMessage !== 'function') {
            console.error('❌ [password.js] postMessage가 함수가 아님');
            setBiometricError('메시지 전송 함수를 찾을 수 없습니다.');
            return;
        }
        
        console.log('👆 [password.js] 생체인식 등록 요청 전송');
        setBiometricError("");
        
        // RN에 생체인식 등록 요청 메시지 전송
        const messageData = {
            type: 'REQUEST_BIOMETRIC_REGISTER',
        };
        const message = JSON.stringify(messageData);
        
        console.log('📤 [password.js] RN에 메시지 전송 시도');
        console.log('   - 메시지 데이터:', messageData);
        console.log('   - 메시지 문자열:', message);
        
        try {
            window.ReactNativeWebView.postMessage(message);
            console.log('✅ [password.js] postMessage 호출 완료');
            
            // 추가 확인: 메시지가 실제로 전송되었는지 확인
            setTimeout(() => {
                console.log('⏱️ [password.js] 메시지 전송 후 1초 경과 - RN에서 응답이 없으면 RN 측 구현을 확인하세요');
            }, 1000);
            
        } catch (error) {
            console.error('❌ [password.js] 메시지 전송 실패:', error);
            console.error('   - Error name:', error?.name);
            console.error('   - Error message:', error?.message);
            console.error('   - Error stack:', error?.stack);
            setBiometricError('생체인식 등록 요청 전송에 실패했습니다: ' + (error?.message || '알 수 없는 오류'));
        }
    };

    // 키패드 숫자 섞기 (비밀번호 모드 변경 시)
    useEffect(() => {
        if (passwordMode) {
            setKeypadNumbers((prev) => shuffleArray(prev));
        }
    }, [passwordMode]);

    // finishLogin 함수 정의 (useEffect보다 먼저 정의되어야 함)
    const finishLogin = useCallback(async (shouldReAuth = false) => {
        // 활성화 후 비밀번호 입력 성공 시
        if (shouldReAuth) {
            console.log('1')
            const existingToken = localStorage.getItem("hb_backendToken");

            console.log("finishLogin 호출 - 토큰 확인:", {
                shouldReAuth,
                hasToken: !!existingToken,
            });

            if (existingToken) {
                console.log(2)
                // 이미 토큰이 있으면 바로 메인으로 이동
                try {
                    const meRes = await fetch(`${API_BASE_URL}/auth/me`, {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${existingToken}`,
                        },
                    });
                    if (meRes.ok) {
                        const meData = await meRes.json();
                        const user = meData.user || {};
                        if (user.name) {
                            localStorage.setItem("hb_serverName", user.name);
                            localStorage.setItem("hb_googleName", user.name);
                        }
                        if (user.email) {
                            localStorage.setItem("hb_serverEmail", user.email);
                            localStorage.setItem("hb_googleEmail", user.email);
                        }
                        if (user.picture) {
                            localStorage.setItem("hb_googlePicture", user.picture);
                        }
                        if (user.id) {
                            localStorage.setItem("hb_backendUserId", String(user.id));
                        }
                    }
                } catch (e) {
                    console.error("Failed to load user info from /auth/me:", e);
                }

                localStorage.setItem("hb_isLoggedIn", "true");
                
                const hasToken = !!localStorage.getItem("hb_backendToken");
                if (!hasToken) {
                    console.error("❌ 토큰이 없습니다! 로그인 페이지로 이동합니다.");
                    navigate("/login", { replace: true });
                    return;
                }
                
                console.log("✅ 활성화 후 비밀번호 입력 성공 - 메인 페이지로 이동");
                navigate("/main", { replace: true });
                return;
            }

            // 토큰이 없으면 로그인 페이지로 이동 (토큰 재발급은 로그인 페이지에서 처리)
            console.log("활성화 후 토큰이 없음 - 로그인 페이지로 이동");
            setError("로그인 정보가 없습니다. 다시 로그인해 주세요.");
            navigate("/login", { replace: true });
            return;
        }

        // 일반적인 비밀번호 입력 성공 시
        // let token = localStorage.getItem("hb_backendToken");
        let token = "123"
        localStorage.setItem("hb_backendToken","123");
        
        // 토큰이 없으면 에러
        if (!token) {
            console.log("비밀번호 입력 성공했지만 토큰이 없음 - 로그인 페이지로 이동");
            setError("로그인 정보가 없습니다. 다시 로그인해 주세요.");
            navigate("/login", { replace: true });
            return;
        }

        localStorage.setItem("hb_isLoggedIn", "true");
        
        console.log("✅ 비밀번호 입력 성공 - 메인 페이지로 이동");
        navigate("/main", { replace: true });
    }, [navigate, setError]);

    // 생체인식 등록 완료 처리
    const handleBiometricRegister = useCallback(async () => {
        console.log('🔐 [password.js] handleBiometricRegister 호출 시작');
        const savedEmail = localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail");
        
        if (!savedEmail) {
            console.error('❌ [password.js] 이메일 정보가 없음');
            setError("이메일 정보가 없습니다.");
            return;
        }

        console.log('📧 [password.js] 등록할 이메일:', savedEmail);
        console.log('🌐 [password.js] API URL:', `${API_BASE_URL}/auth/biometric/register`);

        try {
            // 백엔드에 생체인식 등록 완료 알림
            const registerRes = await fetch(`${API_BASE_URL}/auth/biometric/register`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: savedEmail,
                }),
            });
            
            console.log('📡 [password.js] API 응답 상태:', registerRes.status, registerRes.statusText);
            
            if (registerRes.ok) {
                const registerData = await registerRes.json();
                console.log("✅ [password.js] 생체인식 등록 성공:", registerData);
                setIsBiometricRegistered(true);
                setError("");
                // 등록 완료 후 로그인 완료
                finishLogin();
            } else {
                const errorData = await registerRes.json().catch(() => ({ message: '응답 파싱 실패' }));
                console.error("❌ [password.js] 생체인식 등록 실패:", errorData);
                setError(errorData.message || "생체인식 등록에 실패했습니다.");
            }
        } catch (error) {
            console.error("❌ [password.js] 생체인식 등록 중 오류:", error);
            console.error("   - Error name:", error?.name);
            console.error("   - Error message:", error?.message);
            console.error("   - Error stack:", error?.stack);
            setError("생체인식 등록 중 오류가 발생했습니다: " + (error?.message || '알 수 없는 오류'));
        }
    }, [finishLogin, setError]);

    // RN에서 지문인식 메시지 수신 처리
    useEffect(() => {
        if (!isAppEnvironment()) return;
        
        // RN WebView의 postMessage를 받기 위한 리스너
        const handleRNMessage = (event) => {
            try {
                console.log('📨 [password.js] 메시지 수신:', event);
                
                // React Native WebView는 event.data에 직접 JSON 문자열이 전달됨
                let data;
                if (typeof event.data === 'string') {
                    try {
                        data = JSON.parse(event.data);
                    } catch (e) {
                        // 이미 객체일 수도 있음
                        data = event.data;
                    }
                } else {
                    data = event.data;
                }
                
                console.log('📨 [password.js] 파싱된 데이터:', data);
                
                if (data.type === 'BIOMETRIC_AVAILABLE') {
                    console.log('✅ [password.js] 지문인식 디바이스 지원:', data.available);
                    setIsBiometricDeviceAvailable(data.available || false);
                } else if (data.type === 'FINGERPRINT_SUCCESS') {
                    // 지문인식 성공 시
                    if (passwordMode === 'biometric_register') {
                        // 생체인식 등록 모드일 때
                        console.log('✅ [password.js] 지문인식 등록 성공');
                        setBiometricError("");
                        handleBiometricRegister();
                    } else {
                        // 로그인 모드일 때
                        console.log('✅ [password.js] 지문인식 성공 - 로그인 처리');
                        setBiometricError("");
                        finishLogin();
                    }
                } else if (data.type === 'FINGERPRINT_ERROR') {
                    console.log('❌ [password.js] 지문인식 오류:', data.message);
                    setBiometricError(data.message || '지문인식에 실패했습니다.');
                } else if (data.type === 'BIOMETRIC_REGISTER_SUCCESS') {
                    // 생체인식 등록 성공 (RN에서 직접 등록 완료 메시지)
                    console.log('✅ [password.js] 생체인식 등록 성공');
                    setBiometricError("");
                    handleBiometricRegister();
                }
            } catch (e) {
                console.warn('⚠️ [password.js] 지문인식 메시지 파싱 오류:', e, event);
            }
        };
        
        // document에 이벤트 리스너 추가 (RN WebView가 postMessage로 전달)
        document.addEventListener('message', handleRNMessage);
        
        // window.addEventListener도 추가 (일부 경우 대비)
        window.addEventListener('message', handleRNMessage);
        
        // window.ReactNativeWebView가 있는 경우 직접 리스너 추가
        if (window.ReactNativeWebView) {
            console.log('✅ [password.js] window.ReactNativeWebView 리스너 설정');
        }
        
        return () => {
            document.removeEventListener('message', handleRNMessage);
            window.removeEventListener('message', handleRNMessage);
        };
    }, [passwordMode, handleBiometricRegister, finishLogin]);

    // 비밀번호 입력 완료 처리
    useEffect(() => {
        if (pin.length !== 4) return;

        const handlePasswordComplete = async () => {
            if (passwordMode === "set") {
                setFirstOtp(pin);
                setPin([]);
                setPasswordMode("confirm");
            } else if (passwordMode === "confirm") {
                if (JSON.stringify(firstOtp) === JSON.stringify(pin)) {
                    // OTP를 DB에 저장 (API 호출)
                    const otpString = pin.join("");
                    const savedEmail = localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail");
                    
                    if (savedEmail) {
                        try {
                            const setRes = await fetch(`${API_BASE_URL}/auth/otp/set`, {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                    otp: otpString,
                                    email: savedEmail,
                                }),
                            });
                            
                            if (setRes.ok) {
                                const setData = await setRes.json();
                                console.log("OTP 저장 성공:", setData);
                                setError("");
                                
                                // 앱 환경이고 디바이스가 생체인식을 지원하면 생체인식 등록 화면으로 이동
                                const isApp = isAppEnvironment();
                                if (isApp && isBiometricDeviceAvailable) {
                                    console.log("📱 앱 환경 - 생체인식 등록 화면으로 이동");
                                    setPasswordMode("biometric_register");
                                } else {
                                    // 웹 환경이거나 생체인식이 사용 불가능하면 바로 로그인 완료
                                    finishLogin();
                                }
                            } else {
                                const errorData = await setRes.json();
                                setError(errorData.message || "OTP 저장에 실패했습니다.");
                                setFirstOtp(null);
                                setPin([]);
                                setPasswordMode("set");
                            }
                        } catch (error) {
                            console.error("OTP 저장 중 오류:", error);
                            setError("OTP 저장 중 오류가 발생했습니다.");
                            setFirstOtp(null);
                            setPin([]);
                            setPasswordMode("set");
                        }
                    } else {
                        setError("이메일 정보가 없습니다.");
                        setFirstOtp(null);
                        setPin([]);
                        setPasswordMode("set");
                    }
                } else {
                    setError("비밀번호가 일치하지 않습니다.");
                    setFirstOtp(null);
                    setPin([]);
                    setPasswordMode("set");
                }
            } else if (passwordMode === "enter") {
                // DB에서 OTP 확인 (API 호출)
                const otpString = pin.join("");
                const savedEmail = localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail");
                
                if (savedEmail) {
                    try {
                        const verifyRes = await fetch(`${API_BASE_URL}/auth/otp/verify`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                otp: otpString,
                                email: savedEmail,
                            }),
                        });
                        
                        if (verifyRes.ok) {
                            const verifyData = await verifyRes.json();
                            if (verifyData.ok) {
                                setError("");
                                
                                // 앱 환경이고 생체인식 디바이스가 지원되면 생체인식 등록 여부 확인 (UI 표시용)
                                const isApp = isAppEnvironment();
                                if (isApp && isBiometricDeviceAvailable) {
                                    try {
                                        // 생체인식 등록 여부 직접 확인 (최신 상태)
                                        const checkRes = await fetch(`${API_BASE_URL}/auth/biometric/check?email=${encodeURIComponent(savedEmail)}`, {
                                            method: "GET",
                                            headers: {
                                                "Content-Type": "application/json",
                                            },
                                        });
                                        
                                        if (checkRes.ok) {
                                            const checkData = await checkRes.json();
                                            setIsBiometricRegistered(checkData.isRegistered || false);
                                        }
                                    } catch (error) {
                                        console.error("생체인식 등록 여부 확인 중 오류:", error);
                                        setIsBiometricRegistered(false);
                                    }
                                }
                                
                                const isAfterActivate = userStatus === "active";
                                console.log("비밀번호 입력 성공, finishLogin 호출:", { isAfterActivate, userStatus });
                                finishLogin(isAfterActivate);
                            } else {
                                setError(verifyData.message || "비밀번호가 올바르지 않습니다.");
                                setPin([]);
                            }
                        } else {
                            const errorData = await verifyRes.json();
                            if (errorData.needsSetup) {
                                // OTP가 설정되지 않은 경우 설정 모드로 전환
                                setError("");
                                setPasswordMode("set");
                            } else {
                                setError(errorData.message || "비밀번호가 올바르지 않습니다.");
                                setPin([]);
                            }
                        }
                    } catch (error) {
                        console.error("OTP 확인 중 오류:", error);
                        setError("OTP 확인 중 오류가 발생했습니다.");
                        setPin([]);
                    }
                } else {
                    setError("이메일 정보가 없습니다.");
                    setPin([]);
                }
            }
        };

        handlePasswordComplete();
    }, [pin, passwordMode, firstOtp, userStatus, finishLogin, navigate]);

    const pressKey = (key) => {
        if (key === "CLEAR") {
            setPin([]);
            setError("");
            return;
        }
        if (key === "BACK") {
            setPin((p) => p.slice(0, -1));
            setError("");
            return;
        }
        // 숫자 타입 체크 개선 (number 타입이거나 숫자로 변환 가능한 경우)
        const numKey = typeof key === "number" ? key : Number(key);
        if (!isNaN(numKey) && numKey >= 0 && numKey <= 9) {
            setError("");
            setPin((p) => (p.length >= 4 ? p : [...p, numKey]));
        }
    };

    // 뒤로가기 버튼 클릭
    const handleBack = () => {
        navigate("/login", { replace: true });
    };

    // 마지막 줄 배치용 숫자 분리
    const topNineNumbers = keypadNumbers.slice(0, 9);
    const lastNumber = keypadNumbers[9];

    if (!passwordMode) {
        return null; // 모드 결정 중
    }

    return (
        <div className="container-wrap login-wrap">
            <div className="login-top-logo">
                <img src="/img/hongik_logo.png" alt="탐정법인홍익" />
            </div>

            {/* 뒤로가기 버튼 */}
            <button
                onClick={handleBack}
                style={{
                    position: "absolute",
                    top: "20px",
                    left: "20px",
                    background: "transparent",
                    border: "none",
                    fontSize: "20px",
                    cursor: "pointer",
                    color: "#666",
                    zIndex: 10,
                }}
            >
                <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <div className="login-main">
                {userEmail && (
                    <div className="login-user-card">
                        <div className="login-user-name">
                            {userName || "이름 미지정"}
                        </div>
                        <div className="login-user-email">{userEmail}</div>
                    </div>
                )}

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                    }}
                >
                    <div className="login-title">
                        {passwordMode === "set" && "비밀번호 설정"}
                        {passwordMode === "confirm" && "비밀번호 확인"}
                        {passwordMode === "enter" && "비밀번호 입력"}
                        {passwordMode === "biometric_register" && "생체인식 등록"}
                    </div>
                    <div className="login-pin-dots">
                        {[0, 1, 2, 3].map((i) => (
                            <span
                                key={i}
                                className={`login-dot ${pin[i] !== undefined ? "filled" : ""}`}
                            />
                        ))}
                    </div>
                    {error ? (
                        <div className="login-error-message" style={{ marginTop: 12 }}>
                            {error}
                        </div>
                    ) : biometricError ? (
                        <div className="login-error-message" style={{ marginTop: 12, color: "#ff4444" }}>
                            {biometricError}
                        </div>
                    ) : (
                        <div
                            className="password-guide-message"
                            style={{
                                marginTop: 12,
                                fontSize: "16px",
                                textAlign: "center",
                                color: "#6c6c6c",
                            }}
                        >
                            {passwordMode === "set" && "초기 비밀번호를 설정하세요"}
                            {passwordMode === "confirm" && "초기 비밀번호를 한번 더 입력해주세요."}
                            {passwordMode === "enter" && "비밀번호를 입력하여 서비스를 이용해주세요."}
                            {passwordMode === "biometric_register" && "생체인식을 등록하여 빠른 로그인을 사용하세요."}
                        </div>
                    )}
                    
                    {/* 생체인식 등록 화면 (앱 환경일 때만) */}
                    {passwordMode === "biometric_register" && isAppEnvironment() && (
                        <div style={{ marginTop: 40, display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
                            <div style={{ fontSize: "14px", color: "#6c6c6c", textAlign: "center", marginBottom: 8 }}>
                                생체인식을 등록하면 다음 로그인부터<br />지문으로 빠르게 로그인할 수 있습니다.
                            </div>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('🔘 [password.js] 생체인식 등록 버튼 클릭');
                                    requestBiometricRegister();
                                }}
                                style={{
                                    width: "90%",
                                    maxWidth: "320px",
                                    padding: "16px 24px",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    color: "#fff",
                                    backgroundColor: "#4285f4",
                                    border: "none",
                                    borderRadius: "12px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "12px",
                                    boxShadow: "0 2px 8px rgba(66, 133, 244, 0.3)",
                                }}
                            >
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M12 11c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0 2c1.34 0 4 .67 4 2v1H8v-1c0-1.33 2.66-2 4-2zm0-8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z" />
                                </svg>
                                생체인식 등록하기
                            </button>
                            <button
                                onClick={() => {
                                    // 생체인식 등록 건너뛰기
                                    console.log("생체인식 등록 건너뛰기");
                                    finishLogin();
                                }}
                                style={{
                                    width: "90%",
                                    maxWidth: "320px",
                                    padding: "16px 24px",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    color: "#666",
                                    backgroundColor: "transparent",
                                    border: "1px solid #ddd",
                                    borderRadius: "12px",
                                    cursor: "pointer",
                                }}
                            >
                                나중에 하기
                            </button>
                        </div>
                    )}
                    
                    {/* 지문인식 버튼 (앱 환경이고 비밀번호 입력 모드이며 생체인식이 등록되어 있을 때만) */}
                    {isAppEnvironment() && passwordMode === "enter" && isBiometricDeviceAvailable && isBiometricRegistered && (
                        <div style={{ marginTop: 24, display: "flex", justifyContent: "center" }}>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    requestFingerprint();
                                }}
                                style={{
                                    width: "100%",
                                    maxWidth: "320px",
                                    padding: "12px 24px",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    color: "#fff",
                                    backgroundColor: "#4285f4",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "8px",
                                }}
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M12 11c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0 2c1.34 0 4 .67 4 2v1H8v-1c0-1.33 2.66-2 4-2zm0-8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z" />
                                </svg>
                                지문으로 로그인
                            </button>
                        </div>
                    )}
                </div>

                {/* 키패드는 생체인식 등록 모드가 아닐 때만 표시 */}
                {passwordMode !== "biometric_register" && (
                    <div className="login-keypad">
                        {/* 위 3줄: 랜덤 9개 숫자 (3x3) */}
                        {topNineNumbers.map((n) => (
                            <button 
                                key={n} 
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    pressKey(n);
                                }}
                            >
                                {n}
                            </button>
                        ))}
                        {/* 마지막 줄: CLEAR / 숫자 / BACK */}
                        <button 
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                pressKey("CLEAR");
                            }}
                        >
                            CLEAR
                        </button>
                        <button 
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                pressKey(lastNumber);
                            }}
                        >
                            {lastNumber}
                        </button>
                        <button 
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                pressKey("BACK");
                            }}
                        >
                            Back
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

